<template>
  <div class="mediation-detail-container animated fadeInRight">
    <el-page-header @back="_goBack" content="调解案件详情" style="margin-bottom:20px" />

    <div v-loading="loading">
      <!-- 案件基本信息 -->
      <el-card>
        <div slot="header" class="flex justify-between">
          <span>案件基本信息</span>
          <div>
            <el-button size="small" @click="_printForm">打印登记表</el-button>
            <el-button
              v-if="caseInfo.state === 'PENDING'"
              size="small"
              type="primary"
              @click="_openAudit"
            >受理审查</el-button>
            <el-button
              v-if="caseInfo.state === 'PENDING'"
              size="small"
              type="success"
              @click="_openAcceptCase"
            >合并受理+指派</el-button>
            <el-button
              v-if="caseInfo.state === 'PENDING'"
              size="small"
              type="danger"
              @click="_openRejectCase"
            >不予调解</el-button>
            <el-button
              v-if="caseInfo.state === 'ACCEPTED'"
              size="small"
              type="primary"
              @click="_openAssign"
            >指派调解员</el-button>
            <el-button
              v-if="['SCHEDULED','IN_PROGRESS'].indexOf(caseInfo.state) >= 0"
              size="small"
              type="warning"
              @click="_openChangeMediator"
            >更换调解员</el-button>
            <el-button
              v-if="['ACCEPTED','SCHEDULED','IN_PROGRESS'].indexOf(caseInfo.state) >= 0"
              size="small"
              type="success"
              @click="_openProcess"
            >调解过程记录</el-button>
            <el-button
              v-if="['ACCEPTED','SCHEDULED','IN_PROGRESS'].indexOf(caseInfo.state) >= 0"
              size="small"
              type="success"
              @click="_openAgreement"
            >达成协议</el-button>
            <el-button
              v-if="['ACCEPTED','SCHEDULED','IN_PROGRESS'].indexOf(caseInfo.state) >= 0"
              size="small"
              type="danger"
              @click="_openTerminate"
            >调解终止</el-button>
            <el-button
              v-if="caseInfo.state === 'AGREED'"
              size="small"
              type="primary"
              @click="_openVisit"
            >回访记录</el-button>
            <el-button
              v-if="['AGREED','TERMINATED'].indexOf(caseInfo.state) >= 0"
              size="small"
              type="info"
              @click="_doArchive"
            >归档</el-button>
          </div>
        </div>
        <el-row :gutter="20">
          <el-col :span="8"><b>案件编号：</b>{{ caseInfo.caseId }}</el-col>
          <el-col :span="8"><b>案件状态：</b>
            <el-tag :type="_stateTagType(caseInfo.state)">{{ _stateName(caseInfo.state) }}</el-tag>
          </el-col>
          <el-col :span="8"><b>创建时间：</b>{{ caseInfo.createTime }}</el-col>
          <el-col :span="8"><b>纠纷类别：</b>{{ caseInfo.disputeType }}</el-col>
          <el-col :span="8"><b>调委会：</b>{{ caseInfo.committeeName }}</el-col>
          <el-col :span="8"><b>申请人：</b>{{ caseInfo.applyUserName }}</el-col>
          <el-col :span="8"><b>调解员：</b>{{ caseInfo.mediatorName || '-' }}</el-col>
          <el-col :span="8"><b>视频调解：</b>{{ caseInfo.acceptVideo === '1' ? '是' : '否' }}</el-col>
          <el-col :span="8"><b>优先时段：</b>{{ caseInfo.preferredPeriod || '-' }}</el-col>
          <el-col :span="24"><b>纠纷简述：</b>{{ caseInfo.disputeDesc }}</el-col>
        </el-row>
      </el-card>

      <!-- 当事人信息 -->
      <el-card style="margin-top:20px">
        <div slot="header"><span>当事人信息</span></div>
        <el-table :data="parties" border>
          <el-table-column label="类型" align="center" width="120">
            <template slot-scope="scope">{{ _partyType(scope.row.partyType) }}</template>
          </el-table-column>
          <el-table-column prop="name" label="姓名" align="center" />
          <el-table-column prop="gender" label="性别" align="center" width="80" />
          <el-table-column prop="idCard" label="身份证" align="center" />
          <el-table-column prop="address" label="住址/单位" align="center" show-overflow-tooltip />
          <el-table-column prop="phone" label="联系电话" align="center" />
        </el-table>
      </el-card>

      <!-- 调解员 -->
      <el-card style="margin-top:20px" v-if="caseInfo.mediatorId">
        <div slot="header"><span>调解员</span></div>
        <el-row :gutter="20">
          <el-col :span="12"><b>调解员：</b>{{ caseInfo.mediatorName }}</el-col>
        </el-row>
      </el-card>

      <!-- 时间线 -->
      <el-card style="margin-top:20px">
        <div slot="header"><span>处理时间线</span></div>
        <el-timeline v-if="events.length > 0">
          <el-timeline-item v-for="(evt, idx) in events" :key="idx" :timestamp="evt.createTime" placement="top">
            <el-card>
              <h4>{{ _eventType(evt.eventType) }} {{ evt.operatorName ? '· ' + evt.operatorName : '' }}</h4>
              <p>{{ evt.remark }}</p>
            </el-card>
          </el-timeline-item>
        </el-timeline>
        <div v-else style="text-align:center;color:#999;padding:20px 0">暂无处理记录</div>
      </el-card>

      <!-- 协议信息 -->
      <el-card style="margin-top:20px" v-if="agreement">
        <div slot="header"><span>调解协议</span></div>
        <el-row :gutter="20">
          <el-col :span="24"><b>协议内容：</b>{{ agreement.agreementContent }}</el-col>
          <el-col :span="8"><b>司法确认：</b>{{ agreement.judicialConfirm === '1' ? '是' : '否' }}</el-col>
          <el-col :span="8"><b>签字状态：</b>{{ _signStatus(agreement.signStatus) }}</el-col>
          <el-col :span="24" v-if="agreement.photoUrls" style="margin-top:10px">
            <b>现场照片：</b>
            <div style="display:flex;flex-wrap:wrap;margin-top:10px">
              <el-image
                v-for="(u, i) in agreement.photoUrls.split(',')"
                :key="i"
                :src="u"
                :preview-src-list="agreement.photoUrls.split(',')"
                style="width:120px;height:120px;margin-right:10px;margin-bottom:10px"
                fit="cover"
              />
            </div>
          </el-col>
        </el-row>
      </el-card>

      <!-- 回访记录 -->
      <el-card style="margin-top:20px" v-if="visits.length > 0">
        <div slot="header"><span>回访记录</span></div>
        <el-table :data="visits" border>
          <el-table-column label="回访方式" align="center" width="120">
            <template slot-scope="scope">{{ _visitType(scope.row.visitType) }}</template>
          </el-table-column>
          <el-table-column prop="visitTime" label="回访时间" align="center" />
          <el-table-column prop="fulfillment" label="履行情况" align="center" />
          <el-table-column prop="remark" label="备注" align="center" show-overflow-tooltip />
        </el-table>
      </el-card>
    </div>

    <!-- 受理审查 -->
    <el-dialog title="受理审查" :visible.sync="auditDialog.visible" width="500px" :close-on-click-modal="false">
      <el-form :model="auditDialog" label-width="100px">
        <el-form-item label="审核结果">
          <el-radio-group v-model="auditDialog.auditResult">
            <el-radio label="PASS">受理通过</el-radio>
            <el-radio label="REJECT">不予受理</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item v-if="auditDialog.auditResult === 'PASS'" label="调解员">
          <el-select
            v-model="auditDialog.mediatorId"
            placeholder="选填：通过后立即指派调解员"
            filterable
            clearable
            style="width:100%"
            @change="_onAuditMediatorChange"
          >
            <el-option
              v-for="m in auditDialog.mediatorOptions"
              :key="m.staffId"
              :label="m.staffName + '（' + m.staffId + '）'"
              :value="m.staffId"
            />
          </el-select>
          <div v-if="auditDialog.mediatorOptions.length === 0" style="color:#909399;font-size:12px;margin-top:4px">
            未选择调解员则通过后处于「受理中」，可稍后指派
          </div>
        </el-form-item>
        <el-form-item label="审核意见">
          <el-input type="textarea" v-model="auditDialog.remark" :rows="4" placeholder="请填写审核意见" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="auditDialog.visible = false">取消</el-button>
        <el-button type="primary" @click="_submitAudit" :loading="auditDialog.submitting">确定</el-button>
      </div>
    </el-dialog>

    <!-- 指派调解员 -->
    <el-dialog :title="assignDialog.isChange ? '更换调解员' : '指派调解员'" :visible.sync="assignDialog.visible" width="500px" :close-on-click-modal="false">
      <el-form :model="assignDialog" label-width="100px">
        <el-form-item label="纠纷类型">
          <span>{{ caseInfo.disputeType || '-' }}</span>
        </el-form-item>
        <el-form-item label="调解员" required>
          <el-select
            v-model="assignDialog.mediatorId"
            placeholder="请选择调解员"
            filterable
            style="width:100%"
            @change="_onMediatorChange"
          >
            <el-option
              v-for="m in assignDialog.mediatorOptions"
              :key="m.staffId"
              :label="m.staffName + '（' + m.staffId + '）'"
              :value="m.staffId"
            />
          </el-select>
          <div v-if="assignDialog.mediatorOptions.length === 0" style="color:#f56c6c;font-size:12px;margin-top:4px">
            该纠纷类型未设置调解员，请先到「调解类型管理」中配置
          </div>
        </el-form-item>
        <el-form-item label="更换原因" v-if="assignDialog.isChange">
          <el-input type="textarea" v-model="assignDialog.reason" :rows="3" placeholder="请填写更换原因" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="assignDialog.visible = false">取消</el-button>
        <el-button type="primary" @click="_submitAssign" :loading="assignDialog.submitting">确定</el-button>
      </div>
    </el-dialog>

    <!-- 合并受理+指派 -->
    <el-dialog title="合并受理+指派调解员" :visible.sync="acceptDialog.visible" width="600px" :close-on-click-modal="false">
      <el-form :model="acceptDialog" label-width="100px">
        <el-form-item label="纠纷类型">
          <el-select
            v-model="acceptDialog.disputeTypeId"
            placeholder="可修改纠纷类型"
            filterable
            style="width:100%"
            @change="_onAcceptDisputeTypeChange"
          >
            <el-option
              v-for="t in acceptDialog.disputeTypeOptions"
              :key="t.typeId"
              :label="t.typeName"
              :value="t.typeId"
            />
          </el-select>
          <div style="font-size:12px;color:#999;margin-top:4px">默认使用用户提交的类型，仅当用户选择不准确时才修改</div>
        </el-form-item>
        <el-form-item label="调解员" required>
          <el-select
            v-model="acceptDialog.mediatorId"
            placeholder="请选择调解员"
            filterable
            style="width:100%"
            @change="_onAcceptMediatorChange"
          >
            <el-option
              v-for="m in acceptDialog.mediatorOptions"
              :key="m.staffId"
              :label="m.staffName + '（' + m.staffId + '）'"
              :value="m.staffId"
            />
          </el-select>
          <div v-if="acceptDialog.mediatorOptions.length === 0" style="color:#f56c6c;font-size:12px;margin-top:4px">
            该纠纷类型未设置调解员，请先到「调解类型管理」中配置
          </div>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="acceptDialog.visible = false">取消</el-button>
        <el-button type="primary" @click="_submitAcceptCase" :loading="acceptDialog.submitting">确定受理</el-button>
      </div>
    </el-dialog>

    <!-- 不予调解 -->
    <el-dialog title="不予调解" :visible.sync="rejectDialog.visible" width="500px" :close-on-click-modal="false">
      <el-form :model="rejectDialog" label-width="100px">
        <el-form-item label="不予调解理由" required>
          <el-input type="textarea" v-model="rejectDialog.reason" :rows="4" placeholder="请填写不予调解理由" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="rejectDialog.visible = false">取消</el-button>
        <el-button type="danger" @click="_submitRejectCase" :loading="rejectDialog.submitting">确定不予调解</el-button>
      </div>
    </el-dialog>

    <!-- 调解过程记录 -->
    <el-dialog title="调解过程记录" :visible.sync="processDialog.visible" width="600px" :close-on-click-modal="false">
      <el-input type="textarea" v-model="processDialog.remark" :rows="6" placeholder="请填写调解过程记录" />
      <div slot="footer">
        <el-button @click="processDialog.visible = false">取消</el-button>
        <el-button type="primary" @click="_submitProcess" :loading="processDialog.submitting">确定</el-button>
      </div>
    </el-dialog>

    <!-- 达成协议 -->
    <el-dialog title="达成调解协议" :visible.sync="agreementDialog.visible" width="700px" :close-on-click-modal="false">
      <el-form :model="agreementDialog" label-width="100px">
        <el-form-item label="协议内容">
          <el-input type="textarea" v-model="agreementDialog.agreementContent" :rows="6" placeholder="请填写协议内容" />
        </el-form-item>
        <el-form-item label="司法确认">
          <el-switch v-model="agreementDialog.judicialConfirm" active-value="1" inactive-value="0" />
        </el-form-item>
        <el-form-item label="签字状态">
          <el-select v-model="agreementDialog.signStatus" style="width:200px">
            <el-option label="已签字" value="SIGNED" />
            <el-option label="部分签字" value="PARTIAL" />
            <el-option label="未签字" value="UNSIGNED" />
          </el-select>
        </el-form-item>
        <el-form-item label="现场照片">
          <el-input v-model="agreementDialog.photoUrls" placeholder="多个URL用英文逗号分隔" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="agreementDialog.visible = false">取消</el-button>
        <el-button type="primary" @click="_submitAgreement" :loading="agreementDialog.submitting">确定</el-button>
      </div>
    </el-dialog>

    <!-- 调解终止 -->
    <el-dialog title="调解终止" :visible.sync="terminateDialog.visible" width="500px" :close-on-click-modal="false">
      <el-form :model="terminateDialog" label-width="100px">
        <el-form-item label="终止原因">
          <el-input type="textarea" v-model="terminateDialog.remark" :rows="4" placeholder="请填写终止原因" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="terminateDialog.visible = false">取消</el-button>
        <el-button type="danger" @click="_submitTerminate" :loading="terminateDialog.submitting">确定终止</el-button>
      </div>
    </el-dialog>

    <!-- 回访记录 -->
    <el-dialog title="回访记录" :visible.sync="visitDialog.visible" width="600px" :close-on-click-modal="false">
      <el-form :model="visitDialog" label-width="100px">
        <el-form-item label="回访方式">
          <el-select v-model="visitDialog.visitType" style="width:200px">
            <el-option label="电话" value="PHONE" />
            <el-option label="上门" value="CALL" />
            <el-option label="线上" value="ONLINE" />
          </el-select>
        </el-form-item>
        <el-form-item label="回访时间">
          <el-date-picker v-model="visitDialog.visitTime" type="datetime" value-format="yyyy-MM-dd HH:mm:ss" style="width:100%" />
        </el-form-item>
        <el-form-item label="履行情况">
          <el-input v-model="visitDialog.fulfillment" placeholder="请填写履行情况" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input type="textarea" v-model="visitDialog.remark" :rows="3" placeholder="备注说明" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="visitDialog.visible = false">取消</el-button>
        <el-button type="primary" @click="_submitVisit" :loading="visitDialog.submitting">确定</el-button>
      </div>
    </el-dialog>

    <!-- 调解登记表打印 -->
    <el-dialog title="人民调解登记表" :visible.sync="printDialog.visible" width="800px" :close-on-click-modal="true">
      <div id="mediationPrintArea" class="print-area">
        <h2 style="text-align:center">人民调解登记表</h2>
        <table class="print-table">
          <tr>
            <td class="td-label">案件编号</td>
            <td>{{ caseInfo.caseId }}</td>
            <td class="td-label">创建时间</td>
            <td>{{ caseInfo.createTime }}</td>
          </tr>
          <tr>
            <td class="td-label">调委会</td>
            <td colspan="3">{{ caseInfo.committeeName }}</td>
          </tr>
          <tr>
            <td class="td-label">纠纷类别</td>
            <td colspan="3">{{ caseInfo.disputeType }}</td>
          </tr>
          <tr>
            <td class="td-label">纠纷简述</td>
            <td colspan="3">{{ caseInfo.disputeDesc }}</td>
          </tr>
          <tr>
            <td class="td-section" colspan="4">当事人基本情况</td>
          </tr>
          <tr v-for="(p, i) in parties" :key="i">
            <td class="td-label">{{ _partyType(p.partyType) }}</td>
            <td colspan="3">
              姓名：{{ p.name }} &nbsp;
              性别：{{ p.gender }} &nbsp;
              身份证：{{ p.idCard }}<br/>
              住址/单位：{{ p.address }} &nbsp;
              联系电话：{{ p.phone }}
            </td>
          </tr>
          <tr>
            <td class="td-section" colspan="4">调解情况</td>
          </tr>
          <tr>
            <td class="td-label">是否达成协议</td>
            <td colspan="3">{{ caseInfo.state === 'AGREED' ? '是' : '否' }}</td>
          </tr>
          <tr v-if="agreement">
            <td class="td-label">协议内容</td>
            <td colspan="3">{{ agreement.agreementContent }}</td>
          </tr>
          <tr v-if="agreement && agreement.photoUrls">
            <td class="td-label">现场照片</td>
            <td colspan="3">{{ agreement.photoUrls }}</td>
          </tr>
          <tr>
            <td class="td-section" colspan="4">回访情况</td>
          </tr>
          <tr v-if="visits.length > 0">
            <td colspan="4">
              <div v-for="(v, i) in visits" :key="i" style="margin-bottom:8px">
                {{ _visitType(v.visitType) }} - {{ v.visitTime }} - 履行情况：{{ v.fulfillment }}
                <span v-if="v.remark"> - {{ v.remark }}</span>
              </div>
            </td>
          </tr>
          <tr v-else>
            <td colspan="4" style="text-align:center;color:#999">暂无回访记录</td>
          </tr>
          <tr>
            <td class="td-section" colspan="4">签字盖章</td>
          </tr>
          <tr>
            <td class="td-label">当事人签字</td>
            <td></td>
            <td class="td-label">调解员签字</td>
            <td></td>
          </tr>
          <tr>
            <td class="td-label">调委会盖章</td>
            <td colspan="3"></td>
          </tr>
          <tr>
            <td class="td-label">备注</td>
            <td colspan="3">口头协议，双方当事人签字确认，并附上1-3张调解现场照片</td>
          </tr>
        </table>
      </div>
      <div slot="footer">
        <el-button @click="printDialog.visible = false">关闭</el-button>
        <el-button type="primary" @click="_doPrint">打印</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getMediationCaseDetail,
  auditMediationApply,
  assignMediator,
  changeMediator,
  saveMediationProcess,
  saveMediationAgreement,
  terminateMediation,
  archiveMediation,
  saveMediationVisit,
  stateToName,
  stateToTagType,
  eventTypeToName,
  listDisputeTypes,
  acceptMediationCase,
  rejectMediationCase
} from '@/api/oa/mediationApi'

export default {
  name: 'MediationDetail',
  data() {
    return {
      loading: false,
      caseId: '',
      caseInfo: {},
      parties: [],
      events: [],
      agreement: null,
      visits: [],
      auditDialog: { visible: false, auditResult: 'PASS', remark: '', mediatorId: '', mediatorName: '', mediatorOptions: [], submitting: false },
      assignDialog: {
        visible: false,
        isChange: false,
        mediatorId: '',
        mediatorName: '',
        reason: '',
        submitting: false,
        mediatorOptions: []
      },
      acceptDialog: {
        visible: false,
        disputeTypeId: '',
        disputeTypeOptions: [],
        mediatorId: '',
        mediatorName: '',
        mediatorOptions: [],
        submitting: false
      },
      rejectDialog: { visible: false, reason: '', submitting: false },
      processDialog: { visible: false, remark: '', submitting: false },
      agreementDialog: { visible: false, agreementContent: '', judicialConfirm: '0', signStatus: 'SIGNED', photoUrls: '', submitting: false },
      terminateDialog: { visible: false, remark: '', submitting: false },
      visitDialog: { visible: false, visitType: 'PHONE', visitTime: '', fulfillment: '', remark: '', submitting: false },
      printDialog: { visible: false }
    }
  },
  mounted() {
    this.caseId = this.$route.query.caseId
    if (this.caseId) {
      this._loadDetail()
    }
  },
  methods: {
    _stateName: stateToName,
    _stateTagType: stateToTagType,
    _eventType: eventTypeToName,
    _partyType(t) {
      return t === 'APPLICANT' ? '申请人' : (t === 'RESPONDENT' ? '被申请人' : '当事人')
    },
    _signStatus(s) {
      const m = { UNSIGNED: '未签字', PARTIAL: '部分签字', SIGNED: '已签字' }
      return m[s] || s || '-'
    },
    _visitType(t) {
      const m = { PHONE: '电话', CALL: '上门', ONLINE: '线上' }
      return m[t] || t || '-'
    },
    _goBack() {
      this.$router.push('/oa/mediationList')
    },
    _loadDetail() {
      this.loading = true
      getMediationCaseDetail({ caseId: this.caseId }).then(res => {
        const d = res.data || {}
        this.caseInfo = d.caseInfo || {}
        this.parties = d.parties || []
        this.events = d.events || []
        this.agreement = d.agreement || null
        this.visits = d.visits || []
      }).catch(err => {
        this.$message.error(err || '加载失败')
      }).finally(() => {
        this.loading = false
      })
    },
    _openAudit() {
      this.auditDialog.auditResult = 'PASS'
      this.auditDialog.remark = ''
      this.auditDialog.mediatorId = ''
      this.auditDialog.mediatorName = ''
      this.auditDialog.mediatorOptions = []
      this.auditDialog.visible = true
      this._loadMediatorsByCaseDisputeType(this.caseInfo.disputeTypeId, this.auditDialog)
    },
    _onAuditMediatorChange(val) {
      const opt = this.auditDialog.mediatorOptions.find(o => o.staffId === val)
      this.auditDialog.mediatorName = opt ? opt.staffName : ''
    },
    _submitAudit() {
      if (!this.auditDialog.remark) {
        this.$message.error('请填写审核意见')
        return
      }
      this.auditDialog.submitting = true
      const params = {
        caseId: this.caseId,
        auditResult: this.auditDialog.auditResult,
        remark: this.auditDialog.remark
      }
      auditMediationApply(params).then(() => {
        // 审核通过且选择了调解员时，紧接着指派调解员
        if (this.auditDialog.auditResult === 'PASS' && this.auditDialog.mediatorId) {
          return assignMediator({
            caseId: this.caseId,
            mediatorId: this.auditDialog.mediatorId,
            mediatorName: this.auditDialog.mediatorName
          })
        }
      }).then(() => {
        this.$message.success('审核完成')
        this.auditDialog.visible = false
        this._loadDetail()
      }).catch(err => {
        this.$message.error(err || '审核失败')
      }).finally(() => {
        this.auditDialog.submitting = false
      })
    },
    _openAssign() {
      this.assignDialog.isChange = false
      this.assignDialog.mediatorId = ''
      this.assignDialog.mediatorName = ''
      this.assignDialog.reason = ''
      this.assignDialog.mediatorOptions = []
      this.assignDialog.visible = true
      this._loadMediatorsByCaseDisputeType(this.caseInfo.disputeTypeId, this.assignDialog)
    },
    _openChangeMediator() {
      this.assignDialog.isChange = true
      this.assignDialog.mediatorId = ''
      this.assignDialog.mediatorName = ''
      this.assignDialog.reason = ''
      this.assignDialog.mediatorOptions = []
      this.assignDialog.visible = true
      this._loadMediatorsByCaseDisputeType(this.caseInfo.disputeTypeId, this.assignDialog)
    },
    /**
     * 根据案件纠纷类型ID加载关联调解员列表（用于指派/更换调解员下拉）
     */
    _loadMediatorsByCaseDisputeType(typeId, dialog) {
      if (!typeId) {
        return
      }
      listDisputeTypes({}).then(res => {
        const d = res.data || res || {}
        const types = d.disputeTypes || []
        const t = types.find(x => x.typeId === typeId)
        if (t && t.mediators) {
          dialog.mediatorOptions = t.mediators.map(m => ({
            staffId: m.staffId,
            staffName: m.staffName || m.staffId
          }))
        }
      }).catch(() => {
        // 忽略
      })
    },
    _onMediatorChange(val) {
      const opt = this.assignDialog.mediatorOptions.find(o => o.staffId === val)
      this.assignDialog.mediatorName = opt ? opt.staffName : ''
    },
    _submitAssign() {
      if (!this.assignDialog.mediatorId) {
        this.$message.error('请选择调解员')
        return
      }
      this.assignDialog.submitting = true
      const params = {
        caseId: this.caseId,
        mediatorId: this.assignDialog.mediatorId,
        mediatorName: this.assignDialog.mediatorName,
        reason: this.assignDialog.reason
      }
      const fn = this.assignDialog.isChange ? changeMediator : assignMediator
      fn(params).then(() => {
        this.$message.success(this.assignDialog.isChange ? '更换成功' : '指派成功')
        this.assignDialog.visible = false
        this._loadDetail()
      }).catch(err => {
        this.$message.error(err || '操作失败')
      }).finally(() => {
        this.assignDialog.submitting = false
      })
    },
    /**
     * 合并受理+指派
     */
    _openAcceptCase() {
      this.acceptDialog.disputeTypeId = this.caseInfo.disputeTypeId || ''
      this.acceptDialog.mediatorId = ''
      this.acceptDialog.mediatorName = ''
      this.acceptDialog.mediatorOptions = []
      this.acceptDialog.disputeTypeOptions = []
      this.acceptDialog.visible = true
      // 加载所有纠纷类型供选择
      listDisputeTypes({}).then(res => {
        const d = res.data || res || {}
        this.acceptDialog.disputeTypeOptions = d.disputeTypes || []
        // 加载当前类型关联调解员
        this._loadMediatorsForAccept(this.acceptDialog.disputeTypeId)
      }).catch(() => {
        // 忽略
      })
    },
    _loadMediatorsForAccept(typeId) {
      if (!typeId) {
        this.acceptDialog.mediatorOptions = []
        return
      }
      const t = this.acceptDialog.disputeTypeOptions.find(x => x.typeId === typeId)
      if (t && t.mediators) {
        this.acceptDialog.mediatorOptions = t.mediators.map(m => ({
          staffId: m.staffId,
          staffName: m.staffName || m.staffId
        }))
      } else {
        this.acceptDialog.mediatorOptions = []
      }
    },
    _onAcceptDisputeTypeChange(val) {
      this.acceptDialog.mediatorId = ''
      this.acceptDialog.mediatorName = ''
      this._loadMediatorsForAccept(val)
    },
    _onAcceptMediatorChange(val) {
      const opt = this.acceptDialog.mediatorOptions.find(o => o.staffId === val)
      this.acceptDialog.mediatorName = opt ? opt.staffName : ''
    },
    _submitAcceptCase() {
      if (!this.acceptDialog.mediatorId) {
        this.$message.error('请选择调解员')
        return
      }
      this.acceptDialog.submitting = true
      const params = {
        caseId: this.caseId,
        mediatorId: this.acceptDialog.mediatorId,
        mediatorName: this.acceptDialog.mediatorName
      }
      // 仅当管理员修改了纠纷类型时才传 disputeTypeId
      if (this.acceptDialog.disputeTypeId && this.acceptDialog.disputeTypeId !== this.caseInfo.disputeTypeId) {
        params.disputeTypeId = this.acceptDialog.disputeTypeId
      }
      acceptMediationCase(params).then(() => {
        this.$message.success('受理成功')
        this.acceptDialog.visible = false
        this._loadDetail()
      }).catch(err => {
        this.$message.error(err || '受理失败')
      }).finally(() => {
        this.acceptDialog.submitting = false
      })
    },
    /**
     * 不予调解
     */
    _openRejectCase() {
      this.rejectDialog.reason = ''
      this.rejectDialog.visible = true
    },
    _submitRejectCase() {
      if (!this.rejectDialog.reason) {
        this.$message.error('请填写不予调解理由')
        return
      }
      this.rejectDialog.submitting = true
      rejectMediationCase({
        caseId: this.caseId,
        reason: this.rejectDialog.reason
      }).then(() => {
        this.$message.success('已提交不予调解')
        this.rejectDialog.visible = false
        this._loadDetail()
      }).catch(err => {
        this.$message.error(err || '操作失败')
      }).finally(() => {
        this.rejectDialog.submitting = false
      })
    },
    _openProcess() {
      this.processDialog.remark = ''
      this.processDialog.visible = true
    },
    _submitProcess() {
      if (!this.processDialog.remark) {
        this.$message.error('请填写调解过程记录')
        return
      }
      this.processDialog.submitting = true
      saveMediationProcess({
        caseId: this.caseId,
        remark: this.processDialog.remark
      }).then(() => {
        this.$message.success('保存成功')
        this.processDialog.visible = false
        this._loadDetail()
      }).catch(err => {
        this.$message.error(err || '保存失败')
      }).finally(() => {
        this.processDialog.submitting = false
      })
    },
    _openAgreement() {
      this.agreementDialog.agreementContent = ''
      this.agreementDialog.judicialConfirm = '0'
      this.agreementDialog.signStatus = 'SIGNED'
      this.agreementDialog.photoUrls = ''
      this.agreementDialog.visible = true
    },
    _submitAgreement() {
      if (!this.agreementDialog.agreementContent) {
        this.$message.error('请填写协议内容')
        return
      }
      this.agreementDialog.submitting = true
      saveMediationAgreement({
        caseId: this.caseId,
        agreementContent: this.agreementDialog.agreementContent,
        judicialConfirm: this.agreementDialog.judicialConfirm,
        signStatus: this.agreementDialog.signStatus,
        photoUrls: this.agreementDialog.photoUrls
      }).then(() => {
        this.$message.success('保存成功')
        this.agreementDialog.visible = false
        this._loadDetail()
      }).catch(err => {
        this.$message.error(err || '保存失败')
      }).finally(() => {
        this.agreementDialog.submitting = false
      })
    },
    _openTerminate() {
      this.terminateDialog.remark = ''
      this.terminateDialog.visible = true
    },
    _submitTerminate() {
      if (!this.terminateDialog.remark) {
        this.$message.error('请填写终止原因')
        return
      }
      this.terminateDialog.submitting = true
      terminateMediation({
        caseId: this.caseId,
        remark: this.terminateDialog.remark
      }).then(() => {
        this.$message.success('终止成功')
        this.terminateDialog.visible = false
        this._loadDetail()
      }).catch(err => {
        this.$message.error(err || '操作失败')
      }).finally(() => {
        this.terminateDialog.submitting = false
      })
    },
    _doArchive() {
      this.$confirm('确认归档此案件？归档后将不可再修改。', '归档确认', {
        type: 'warning'
      }).then(() => {
        archiveMediation({ caseId: this.caseId }).then(() => {
          this.$message.success('归档成功')
          this._loadDetail()
        }).catch(err => {
          this.$message.error(err || '归档失败')
        })
      }).catch(() => {})
    },
    _openVisit() {
      this.visitDialog.visitType = 'PHONE'
      const now = new Date()
      const pad = n => (n < 10 ? '0' + n : n)
      this.visitDialog.visitTime = now.getFullYear() + '-' + pad(now.getMonth() + 1) + '-' + pad(now.getDate()) +
        ' ' + pad(now.getHours()) + ':' + pad(now.getMinutes()) + ':' + pad(now.getSeconds())
      this.visitDialog.fulfillment = ''
      this.visitDialog.remark = ''
      this.visitDialog.visible = true
    },
    _submitVisit() {
      if (!this.visitDialog.visitType || !this.visitDialog.fulfillment) {
        this.$message.error('请填写回访信息')
        return
      }
      this.visitDialog.submitting = true
      saveMediationVisit({
        caseId: this.caseId,
        visitType: this.visitDialog.visitType,
        visitTime: this.visitDialog.visitTime,
        fulfillment: this.visitDialog.fulfillment,
        remark: this.visitDialog.remark
      }).then(() => {
        this.$message.success('保存成功')
        this.visitDialog.visible = false
        this._loadDetail()
      }).catch(err => {
        this.$message.error(err || '保存失败')
      }).finally(() => {
        this.visitDialog.submitting = false
      })
    },
    _printForm() {
      this.printDialog.visible = true
    },
    _doPrint() {
      const printContent = document.getElementById('mediationPrintArea').innerHTML
      const win = window.open('', '_blank')
      win.document.write('<html><head><title>人民调解登记表</title>')
      win.document.write('<style>')
      win.document.write('body{font-family:"SimSun","Microsoft YaHei",serif;padding:20px}')
      win.document.write('h2{text-align:center;margin-bottom:20px}')
      win.document.write('table{width:100%;border-collapse:collapse;font-size:14px}')
      win.document.write('td,th{border:1px solid #333;padding:8px;vertical-align:top}')
      win.document.write('.td-label{width:120px;background:#f0f0f0;font-weight:bold}')
      win.document.write('.td-section{background:#e0e0e0;font-weight:bold;text-align:center}')
      win.document.write('</style></head><body>')
      win.document.write(printContent)
      win.document.write('</body></html>')
      win.document.close()
      win.focus()
      setTimeout(() => {
        win.print()
      }, 300)
    }
  }
}
</script>

<style>
.mediation-detail-container {
  padding: 20px;
}

.print-area {
  font-family: "SimSun", "Microsoft YaHei", serif;
}

.print-table {
  width: 100%;
  border-collapse: collapse;
}

.print-table td {
  border: 1px solid #333;
  padding: 8px;
  vertical-align: top;
  font-size: 14px;
}

.print-table .td-label {
  width: 120px;
  background: #f0f0f0;
  font-weight: bold;
}

.print-table .td-section {
  background: #e0e0e0;
  font-weight: bold;
  text-align: center;
}
</style>
